import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { GlycemiaParametersType, glycemiaParametersTypeTranslationMapping } from "@/models/DiabetesDataModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type ThresholdStep = GlycemiaParametersType | "normal";

// UI extension of `GlycemiaParametersType` with the "normal" band; defers to the model mapping for the model values.
export const thresholdStepTranslationMapping = (step: ThresholdStep): string =>
  step === "normal" ? "thresholdSettings.normal" : glycemiaParametersTypeTranslationMapping(step);

export type ThresholdStepProps = PropsWithChildren<{
  id?: string;
  threshold: ThresholdStep;
  className?: string;
}>;

export const ThresholdStep: React.FC<ThresholdStepProps> = ({ id, threshold, children, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  return (
    <div id={id} className={cx(styles.container, className)}>
      <div className={cx(styles.box, styles[threshold])}>
        <Typography variant="body">{t(thresholdStepTranslationMapping(threshold))}</Typography>
      </div>
      {children}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  box: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    flex: 1 1;
    height: ${theme.spacing(40)};
    aspect-ratio: 3;
    border-radius: ${theme.spacing(4)};
    padding: ${theme.spacing(2)};
    overflow: clip;
  `,
  hypoglycemia: css`
    background-color: ${theme.palette.glycemia.hypoglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.hypoglycemia.main};
  `,
  hyperglycemia: css`
    background-color: ${theme.palette.glycemia.hyperglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.hyperglycemia.main};
  `,
  severeHypoglycemia: css`
    background-color: ${theme.palette.glycemia.severeHypoglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.severeHypoglycemia.main};
  `,
  severeHyperglycemia: css`
    background-color: ${theme.palette.glycemia.severeHyperglycemia.contrastText};
    border: 1px solid ${theme.palette.glycemia.severeHyperglycemia.main};
  `,
  normal: css`
    background-color: ${theme.palette.glycemia.normal.contrastText};
    border: 1px solid ${theme.palette.glycemia.normal.main};
  `,
});
